import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from './Toast';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, profile, signOut, role, company, memberships, switchCompany, joinCompany, createCompany } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [dropdownOpen,   setDropdownOpen]   = useState(false);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [showJoinForm,   setShowJoinForm]   = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [joinCode,       setJoinCode]       = useState('');
  const [joinRole,       setJoinRole]       = useState<'developer' | 'tester'>('developer');
  const [joinLoading,    setJoinLoading]    = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [createLoading,  setCreateLoading]  = useState(false);
  const [switching,      setSwitching]      = useState<string | null>(null); // company_id being switched to

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setShowJoinForm(false);
        setShowCreateForm(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    await signOut();
    navigate('/login');
  };

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === company?.id || switching) return;
    setSwitching(companyId);
    const { error } = await switchCompany(companyId);
    setSwitching(null);
    if (error) {
      showToast('Failed to switch company.', 'error');
    } else {
      setDropdownOpen(false);
      navigate('/');
    }
  };

  const handleJoinCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoinLoading(true);
    const { error, company_name } = await joinCompany(joinCode, joinRole);
    setJoinLoading(false);
    if (error) {
      const msg = error.message?.includes('Invalid invite code')
        ? 'Invalid invite code. Check and try again.'
        : error.message?.includes('Already a member')
        ? 'You are already a member of this company.'
        : 'Failed to join company.';
      showToast(msg, 'error');
    } else {
      showToast(`Joined ${company_name ?? 'new company'} successfully!`, 'success');
      setJoinCode('');
      setShowJoinForm(false);
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;
    setCreateLoading(true);
    const { error, company_name } = await createCompany(newCompanyName, false);
    setCreateLoading(false);
    if (error) {
      showToast(error.message || 'Failed to create company.', 'error');
      return;
    }
    showToast(`${company_name ?? 'Company'} created successfully!`, 'success');
    setNewCompanyName('');
    setShowCreateForm(false);
  };

  const userEmail   = user?.email ?? '';
  const displayName = profile?.full_name?.trim() || userEmail.split('@')[0];
  const initials    = displayName.charAt(0).toUpperCase();

  const roleBadgeColor: Record<string, string> = {
    admin:     '#1C9B82',
    developer: '#3b82f6',
    tester:    '#f59e0b',
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">

        {/* Brand */}
        <Link to="/" className="navbar-brand" aria-label="Naipeak Issue Tracker — Home">
          <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true">
            <defs>
              <linearGradient id="nb-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1C9B82"/>
                <stop offset="100%" stopColor="#00796B"/>
              </linearGradient>
            </defs>
            <rect width="32" height="32" rx="7" fill="url(#nb-grad)"/>
            <polyline points="7.5,17 13,22.5 24.5,10.5" fill="none" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="brand-name">
            <span className="brand-naipeak">Naipeak</span>
            <span className="brand-product">Issue Tracker</span>
          </span>
        </Link>

        {!user && (
          <div className="navbar-auth-links">
            <Link to="/login"  className="navbar-auth-login">Log in</Link>
            <Link to="/signup" className="navbar-auth-signup">Sign up free</Link>
          </div>
        )}

        {user && (
          <>
            {/* Desktop nav links */}
            <div className="navbar-links">
              <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
                Projects
              </Link>
              <Link to="/tasks" className={`nav-link ${location.pathname.startsWith('/tasks') ? 'active' : ''}`}>
                Tasks
              </Link>
              {role === 'admin' && (
                <Link to="/users" className={`nav-link ${location.pathname.startsWith('/users') ? 'active' : ''}`}>
                  Users
                </Link>
              )}
              {role === 'super_admin' && (
                <Link to="/super-admin" className={`nav-link ${location.pathname.startsWith('/super-admin') ? 'active' : ''}`}>
                  Admin Console
                </Link>
              )}
            </div>

            {/* Notification bell */}
            <NotificationBell />

            {/* Theme toggle */}
            <button
              type="button"
              className="theme-toggle-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {resolvedTheme === 'dark' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="m4.93 4.93 1.41 1.41" />
                  <path d="m17.66 17.66 1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="m6.34 17.66-1.41 1.41" />
                  <path d="m19.07 4.93-1.41 1.41" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />
                </svg>
              )}
            </button>

            {/* User dropdown */}
            <div className="navbar-user" ref={dropdownRef}>
              <button
                className={`user-avatar-btn${dropdownOpen ? ' open' : ''}`}
                onClick={() => { setDropdownOpen(o => !o); setShowJoinForm(false); setShowCreateForm(false); }}
                aria-label="Open user menu"
              >
                <span className="user-avatar">{initials}</span>
                <svg
                  className={`avatar-chevron${dropdownOpen ? ' rotated' : ''}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  width="13" height="13"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  {/* User info header */}
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">{initials}</div>
                    <div className="dropdown-info">
                      <div className="dropdown-name">{displayName}</div>
                      <div className="dropdown-email">{userEmail}</div>
                    </div>
                  </div>

                  {/* Active company + role */}
                  <div className="dropdown-meta">
                    <span className="dropdown-company">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      {company?.name ?? 'Company'}
                    </span>
                    <span
                      className="dropdown-role-badge"
                      style={{ background: roleBadgeColor[role ?? ''] ?? '#6b7280' }}
                    >
                      {role ?? 'member'}
                    </span>
                  </div>

                  <hr className="dropdown-divider" />

                  {/* ── Company switcher ── */}
                  <div className="company-switcher-section">
                    <div className="company-switcher-label">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                        <rect x="2" y="7" width="20" height="14" rx="2"/>
                        <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                      </svg>
                      Companies
                    </div>

                    {memberships.map(m => {
                      const isActive  = m.company_id === company?.id;
                      const isLoading = switching === m.company_id;
                      return (
                        <button
                          key={m.company_id}
                          className={`company-switch-item${isActive ? ' active' : ''}`}
                          onClick={() => handleSwitchCompany(m.company_id)}
                          disabled={isActive || !!switching}
                        >
                          <div className="company-switch-avatar">
                            {(m.companies?.name ?? 'C').charAt(0).toUpperCase()}
                          </div>
                          <div className="company-switch-info">
                            <span className="company-switch-name">{m.companies?.name ?? m.company_id}</span>
                            <span
                              className="company-switch-role"
                              style={{ color: roleBadgeColor[m.role] ?? '#6b7280' }}
                            >
                              {m.role}
                            </span>
                          </div>
                          <div className="company-switch-end">
                            {isLoading ? (
                              <span className="btn-spinner" style={{ width: 13, height: 13 }} />
                            ) : isActive ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" style={{ color: '#1C9B82' }}>
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" style={{ color: 'var(--color-text-muted)', opacity: 0.5 }}>
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}

                    {/* Join another company */}
                    {!showJoinForm ? (
                      <button
                        className="company-join-btn"
                        onClick={() => {
                          setShowJoinForm(true);
                          setShowCreateForm(false);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="16"/>
                          <line x1="8" y1="12" x2="16" y2="12"/>
                        </svg>
                        Join another company
                      </button>
                    ) : (
                      <form className="company-join-form" onSubmit={handleJoinCompany}>
                        <input
                          className="company-join-input"
                          placeholder="Invite code (e.g. AB12CD34)"
                          value={joinCode}
                          onChange={e => setJoinCode(e.target.value.toUpperCase())}
                          autoFocus
                          maxLength={12}
                        />
                        <select
                          className="company-join-select"
                          value={joinRole}
                          onChange={e => setJoinRole(e.target.value as 'developer' | 'tester')}
                        >
                          <option value="developer">Developer</option>
                          <option value="tester">Tester</option>
                        </select>
                        <div className="company-join-actions">
                          <button type="submit" className="company-join-submit" disabled={joinLoading || !joinCode.trim()}>
                            {joinLoading ? 'Joining…' : 'Join'}
                          </button>
                          <button type="button" className="company-join-cancel" onClick={() => { setShowJoinForm(false); setJoinCode(''); }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}

                    {role === 'admin' && (
                      !showCreateForm ? (
                        <button
                          className="company-join-btn"
                          onClick={() => {
                            setShowCreateForm(true);
                            setShowJoinForm(false);
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                            <path d="M3 21h18" />
                            <path d="M5 21V7l8-4 6 4v14" />
                            <path d="M9 12h6" />
                          </svg>
                          Create new company
                        </button>
                      ) : (
                        <form className="company-join-form" onSubmit={handleCreateCompany}>
                          <input
                            className="company-join-input"
                            placeholder="Company name"
                            value={newCompanyName}
                            onChange={e => setNewCompanyName(e.target.value)}
                            autoFocus
                            maxLength={100}
                          />
                          <div className="company-join-actions">
                            <button type="submit" className="company-join-submit" disabled={createLoading || !newCompanyName.trim()}>
                              {createLoading ? 'Creating…' : 'Create'}
                            </button>
                            <button
                              type="button"
                              className="company-join-cancel"
                              onClick={() => { setShowCreateForm(false); setNewCompanyName(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      )
                    )}
                  </div>

                  <hr className="dropdown-divider" />

                  {/* Actions */}
                  <Link className="dropdown-item" to="/profile" onClick={() => setDropdownOpen(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    Profile
                  </Link>
                  <Link className="dropdown-item" to="/settings" onClick={() => setDropdownOpen(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                    </svg>
                    Settings
                  </Link>

                  <hr className="dropdown-divider" />

                  <button className="dropdown-item danger" onClick={handleSignOut}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className={`navbar-mobile-toggle${mobileOpen ? ' open' : ''}`}
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          </>
        )}
      </div>

      {/* Mobile menu */}
      {mobileOpen && user && (
        <div className="mobile-menu">
          <div className="mobile-user-info">
            <div className="mobile-user-avatar">{initials}</div>
            <div>
              <div className="mobile-user-name">{displayName}</div>
              <div className="mobile-user-email">{userEmail}</div>
            </div>
          </div>
          <hr className="dropdown-divider" />

          {/* Mobile company switcher */}
          {memberships.length > 0 && (
            <>
              <div className="company-switcher-label" style={{ padding: '8px 16px 4px' }}>Companies</div>
              {memberships.map(m => {
                const isActive = m.company_id === company?.id;
                return (
                  <button
                    key={m.company_id}
                    className={`company-switch-item${isActive ? ' active' : ''}`}
                    style={{ padding: '8px 16px' }}
                    onClick={() => { handleSwitchCompany(m.company_id); setMobileOpen(false); }}
                    disabled={isActive || !!switching}
                  >
                    <div className="company-switch-avatar" style={{ width: 26, height: 26, fontSize: 12 }}>
                      {(m.companies?.name ?? 'C').charAt(0).toUpperCase()}
                    </div>
                    <div className="company-switch-info">
                      <span className="company-switch-name">{m.companies?.name ?? m.company_id}</span>
                      <span className="company-switch-role" style={{ color: roleBadgeColor[m.role] }}>{m.role}</span>
                    </div>
                    {isActive && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" style={{ color: '#1C9B82', marginLeft: 'auto' }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
              <hr className="dropdown-divider" />
            </>
          )}

          <Link to="/"         className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Projects</Link>
          <Link to="/tasks"    className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Daily Tasks</Link>
          <Link to="/profile"  className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Profile</Link>
          <Link to="/settings" className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Settings</Link>
          {role === 'admin' && (
            <Link to="/users"  className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Users</Link>
          )}
          {role === 'super_admin' && (
            <Link to="/super-admin" className="mobile-nav-link" onClick={() => setMobileOpen(false)}>Admin Console</Link>
          )}
          <button className="mobile-nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      )}
    </nav>
  );
}
